import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Check, ChevronRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

const fmtDuration = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
};

export const Lessons = () => {
  const { content } = useContent();
  const navigate = useNavigate();
  const { t, L } = useLang();
  const lessons = content?.lessons || [];

  return (
    <section id="lessons" className="bg-ocean-900 py-20 sm:py-28 text-sand-100" data-testid="lessons-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow text-gold mb-4">{t("lessons.eyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
            {t("lessons.title")}
          </h2>
          <p className="text-sand-100/60 mt-4 text-lg">
            {t("lessons.sub.pre")} <span className="text-gold font-semibold">{t("lessons.sub.duration")}</span>{t("lessons.sub.post")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {lessons.map((l, i) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`lesson-card-${l.id}`}
              className="group bg-ocean-800 rounded-2xl overflow-hidden border border-white/10 flex flex-col hover:border-gold/40 transition-colors"
            >
              <div className="relative h-52 overflow-hidden">
                <img src={l.image} alt={`${L(l.name)} - Isola delle Femmine`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean-800 to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[0.6rem] tracking-[0.18em] uppercase bg-gold text-ocean px-3 py-1 rounded-full">
                  {L(l.level)}
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading text-xl font-bold mb-2">{L(l.name)}</h3>
                <p className="text-sand-100/60 text-sm mb-4 flex-1">{L(l.description)}</p>

                <div className="flex items-center gap-4 mb-4 text-sm">
                  <span className="flex items-center gap-1.5 font-mono text-gold">
                    <Clock className="w-4 h-4" /> {fmtDuration(l.duration_minutes || 80)}
                  </span>
                  <span className="font-heading text-xl font-bold">{l.price > 0 ? `${l.currency || "€"}${l.price}` : t("lessons.onRequest")}</span>
                </div>

                <ul className="space-y-1.5 mb-6">
                  {(l.includes || []).map((inc, k) => (
                    <li key={k} className="flex items-center gap-2 text-sm text-sand-100/70">
                      <Check className="w-4 h-4 text-coral shrink-0" /> {L(inc)}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(`/book?lesson=${l.id}`)}
                  data-testid={`lesson-book-${l.id}`}
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold px-5 py-3 rounded-full transition-all hover:scale-[1.02]"
                >
                  BOOK NOW <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
